import * as THREE from 'three';
import type { Network, RoutePattern, ScheduleFile } from '../data/types';
import type { ElevationIndex } from '../geometry/elevation';
import { dwellForStation } from '../data/dwell';
import { lineColor } from '../data/lineColors';
import { activeServices } from './calendar';

export interface RouteGeometry {
  pathId: string;
  points: { x: number; y: number; z: number }[];
  cum: number[];
  total: number;
}

interface Timeline {
  t: number[]; // times (s)
  d: number[]; // distance (m)
  seg: (Profile | null)[]; // movement profile per interval i -> i+1
}

interface Profile {
  ta: number; // acceleration time (s)
  tc: number; // cruise time (s)
  tb: number; // deceleration time (s)
  v: number; // cruise speed (m/s)
  A: number; // acceleration magnitude (m/s^2)
}

/** visual linger at the terminus after final arrival (turnaround assumption) */
export const TERMINUS_DWELL_S = 60;

/**
 * Trapezoidal speed profile (accelerate -> cruise -> decelerate) fitted to a
 * movement of L metres over T seconds. Falls back to a triangle when the
 * segment is too short for a cruise phase.
 */
export function makeProfile(L: number, T: number): Profile | null {
  if (L <= 0 || T <= 0) return null;
  let A = (5 * L) / (T * T);
  A = Math.min(Math.max(A, 0.3), 1.5);
  const amin = (4 * L) / (T * T);
  if (A < amin) A = amin;
  const disc = Math.max(0, A * A * T * T - 4 * A * L);
  const v = (A * T - Math.sqrt(disc)) / 2;
  const ta = v / A;
  const tb = ta;
  const tc = Math.max(0, T - ta - tb);
  return { ta, tc, tb, v, A };
}

/** distance travelled after `u` seconds into a profile */
export function profilePos(p: Profile, u: number): number {
  if (u <= p.ta) return 0.5 * p.A * u * u;
  if (u <= p.ta + p.tc) return 0.5 * p.A * p.ta * p.ta + p.v * (u - p.ta);
  const s = u - p.ta - p.tc;
  return 0.5 * p.A * p.ta * p.ta + p.v * p.tc + p.v * s - 0.5 * p.A * s * s;
}

interface Run {
  line: string;
  geometry: RouteGeometry;
  timeline: Timeline;
  period: number; // reserved (0 in scheduled mode)
  offset: number;
  dwell: number;
  stops: { name: string; distM: number }[];
  destination: string;
  tripId?: string;
  routeId?: string;
}

export interface TrainInfo {
  line: string;
  destination: string;
  tripId?: string;
  routeId?: string;
  currentStop?: string;
  nextStop?: string;
  speedKmh: number;
  distM: number;
}

export interface RunStopInfo {
  name: string;
  distM: number;
  arrivalSec: number | null;
  departureSec: number | null;
}

export function buildRouteGeometry(
  pattern: RoutePattern,
  network: Network,
  elev: ElevationIndex,
  cache: Map<string, RouteGeometry>,
): RouteGeometry | null {
  if (!pattern.pathId) return null;
  const cached = cache.get(pattern.pathId);
  if (cached) return cached;
  const path = network.paths.find((p) => p.id === pattern.pathId);
  if (!path) return null;
  const points = elev.lift(path.coords);
  const cum = [0];
  for (let i = 1; i < points.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z));
  }
  const geo: RouteGeometry = { pathId: path.id, points, cum, total: cum[cum.length - 1] };
  cache.set(path.id, geo);
  return geo;
}

export interface ValidStop {
  distM: number;
  name: string;
  stopId: string;
  stationId: string | null;
}

/** valid stops (with monotone distances) for a pattern */
export function validStops(pattern: RoutePattern): ValidStop[] {
  return pattern.stops
    .filter((s) => s.distM != null && !s.flag)
    .map((s) => ({ distM: s.distM as number, name: s.name, stopId: s.stopId, stationId: s.stationId ?? null }));
}

/**
 * Fill missing stop dwells with the configured values.
 *
 * Real arrival/departure times stay authoritative: a stop with `departure >
 * arrival` is left untouched (and the terminus is never changed). For a stop
 * with no dwell (`departure <= arrival`) the configured dwell (default 25 s,
 * 35 s for Hbf/Heinrich-Heine-Allee) is added, but capped so the train can
 * still reach the next stop's scheduled arrival (maxSpeed ~90 km/h ceiling).
 */
export function injectDwell(
  times: [number, number][],
  stops: { stationId: string | null; distM: number }[],
  maxSpeed = 25,
): [number, number][] {
  const out = times.map((t) => [t[0], t[1]] as [number, number]);
  for (let i = 1; i < times.length - 1; i++) {
    const arr = times[i][0];
    if (times[i][1] > arr) continue;
    const dwell = dwellForStation(stops[i]?.stationId ?? null);
    const nextArr = times[i + 1][0];
    const distToNext = Math.max(0, (stops[i + 1]?.distM ?? 0) - (stops[i]?.distM ?? 0));
    const maxDwell = Math.max(0, nextArr - arr - distToNext / maxSpeed);
    out[i][1] = arr + Math.min(dwell, maxDwell);
  }
  return out;
}

/** profiles for every interval where distance increases (movement) */
function buildSegments(t: number[], d: number[]): (Profile | null)[] {
  const seg: (Profile | null)[] = [];
  for (let i = 0; i < t.length - 1; i++) {
    const dd = d[i + 1] - d[i];
    const dt = t[i + 1] - t[i];
    seg.push(dd > 0 && dt > 0 ? makeProfile(dd, dt) : null);
  }
  return seg;
}

export class Fleet {
  readonly group = new THREE.Group();
  private runs: Run[] = [];
  private geoCache = new Map<string, RouteGeometry>();
  private lineColor = new Map<string, THREE.Color>();
  private vehicleGeo: THREE.BoxGeometry;
  private dotGeo: THREE.CircleGeometry;
  private selected = new Set<string>();
  private meshes: THREE.Mesh[] = [];
  private dots: THREE.Mesh[] = [];
  private labels: THREE.Sprite[] = [];
  private realtimeDelays = new Map<string, number>();
  private basemapOn = false;
  private viewingDistance = 20000;

  constructor() {
    this.group.name = 'trains';
    this.vehicleGeo = new THREE.BoxGeometry(2.6, 3.2, 16);
    this.dotGeo = new THREE.CircleGeometry(1, 20);
  }

  clear() {
    for (const child of [...this.group.children]) {
      this.group.remove(child);
    }
    this.runs = [];
    this.meshes = [];
    this.dots = [];
    this.labels = [];
    this.realtimeDelays = new Map();
  }

  setBasemapOn(on: boolean) {
    this.basemapOn = on;
  }

  setViewingDistance(m: number) {
    this.viewingDistance = m;
  }

  private color(line: string) {
    if (!this.lineColor.has(line)) {
      this.lineColor.set(line, new THREE.Color(lineColor(line)));
    }
    return this.lineColor.get(line)!;
  }

  setScheduledMulti(
    network: Network,
    elev: ElevationIndex,
    schedules: ScheduleFile[],
    date: Date,
    lineFilter: Set<string> | null,
  ) {
    this.clear();
    for (const schedule of schedules) {
      const active = activeServices(schedule, date);
      this.spawnSchedule(network, elev, schedule, lineFilter, active);
    }
    this.spawnMeshes();
  }

  private spawnSchedule(
    network: Network,
    elev: ElevationIndex,
    schedule: ScheduleFile,
    lineFilter: Set<string> | null,
    active: Set<string> | null,
  ) {
    const patternById = new Map(network.patterns.map((p) => [p.id, p]));
    let candidates = schedule.trips.filter((t) => !active || active.has(t.serviceId));
    // limit count: keep an even sample across the service day (documented)
    const MAX = 250;
    if (candidates.length > MAX) {
      candidates.sort((a, b) => a.times[0][0] - b.times[0][0]);
      const step = candidates.length / MAX;
      candidates = Array.from({ length: MAX }, (_, i) => candidates[Math.floor(i * step)]);
    }
    let spawned = 0;
    for (const trip of candidates) {
      if (lineFilter && lineFilter.size && !lineFilter.has(schedule.line)) continue;
      const pattern = patternById.get(trip.patternId);
      if (!pattern) continue;
      const stops = validStops(pattern);
      if (stops.length < 2 || stops.length !== trip.times.length) continue;
      const geo = buildRouteGeometry(pattern, network, elev, this.geoCache);
      if (!geo) continue;
      const times = injectDwell(trip.times, stops);
      const t: number[] = [];
      const d: number[] = [];
      for (let i = 0; i < stops.length; i++) {
        t.push(times[i][0]);
        d.push(stops[i].distM!);
        if (i < stops.length - 1) {
          t.push(times[i][1]);
          d.push(stops[i].distM!);
        }
      }
      // skip non-monotone timelines
      let ok = true;
      for (let i = 1; i < t.length; i++) if (t[i] < t[i - 1] || d[i] < d[i - 1]) { ok = false; break; }
      if (!ok) continue;
      this.runs.push({
        line: schedule.line,
        geometry: geo,
        timeline: { t, d, seg: buildSegments(t, d) },
        period: 0,
        offset: 0,
        dwell: TERMINUS_DWELL_S,
        stops: stops.map((s) => ({ name: s.name, distM: s.distM })),
        destination: stops[stops.length - 1].name,
        tripId: trip.id,
        routeId: trip.routeId,
      });
      spawned++;
    }
    void spawned;
  }

  private spawnMeshes() {
    for (let i = 0; i < this.runs.length; i++) {
      const run = this.runs[i];
      const color = this.color(run.line);
      const mat = new THREE.MeshStandardMaterial({
        color,
        emissive: color.clone().multiplyScalar(0.35),
        roughness: 0.5,
        metalness: 0.2,
      });
      const mesh = new THREE.Mesh(this.vehicleGeo, mat);
      mesh.userData.line = run.line;
      mesh.userData.runIndex = i;
      const label = makeLabelSprite(run.line, color);
      label.position.set(0, 5, 0);
      label.visible = false;
      label.userData.runIndex = i;
      this.group.add(label);
      mesh.userData.label = label;
      this.labels.push(label);

      this.group.add(mesh);
      this.meshes.push(mesh);

      const dot = new THREE.Mesh(
        this.dotGeo,
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.95,
          side: THREE.DoubleSide,
          depthWrite: false,
        }),
      );
      dot.rotation.x = -Math.PI / 2;
      dot.scale.setScalar(6);
      dot.renderOrder = 4;
      dot.visible = false;
      dot.userData.line = run.line;
      dot.userData.runIndex = i;
      this.group.add(dot);
      this.dots.push(dot);
    }
  }

  setLineSelection(selected: Set<string>) {
    this.selected = selected;
    for (const mesh of this.meshes) {
      const line = mesh.userData.line as string;
      mesh.visible = !selected.size || selected.has(line);
    }
    for (const dot of this.dots) {
      const line = dot.userData.line as string;
      dot.visible = !selected.size || selected.has(line);
    }
  }

  get runCount(): number {
    return this.runs.length;
  }

  /** objects that can be picked (clicked/hovered) to identify a train */
  get pickTargets(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    for (let i = 0; i < this.meshes.length; i++) {
      const m = this.meshes[i];
      const dot = this.dots[i];
      if (m.visible) out.push(m);
      if (dot && dot.visible) out.push(dot);
    }
    for (const l of this.labels) if (l.visible) out.push(l);
    return out;
  }

  runIndexFor(object: THREE.Object3D): number | null {
    const i = (object.userData as { runIndex?: number }).runIndex;
    return typeof i === 'number' ? i : null;
  }

  runIndexByTripId(tripId: string): number | null {
    for (let i = 0; i < this.runs.length; i++) if (this.runs[i].tripId === tripId) return i;
    return null;
  }

  realtimeDelayFor(runIndex: number): number | null {
    const run = this.runs[runIndex];
    if (!run || !run.tripId) return null;
    return this.realtimeDelays.get(run.tripId) ?? null;
  }

  /** Apply matched realtime delays (tripId -> seconds) to markers. */
  setRealtimeDelays(delays: Map<string, number>) {
    this.realtimeDelays = delays;
    for (let i = 0; i < this.runs.length; i++) {
      const run = this.runs[i];
      const label = this.meshes[i]?.userData.label as THREE.Sprite | undefined;
      if (!label) continue;
      const delay = run.tripId ? delays.get(run.tripId) ?? null : null;
      refreshLabelSprite(label, delay);
    }
  }

  clearRealtime() {
    this.realtimeDelays = new Map();
    for (let i = 0; i < this.runs.length; i++) {
      const label = this.meshes[i]?.userData.label as THREE.Sprite | undefined;
      if (label) refreshLabelSprite(label, null);
    }
  }

  get visibleTripIds(): Set<string> {
    const ids = new Set<string>();
    for (let i = 0; i < this.runs.length; i++) {
      if (this.meshes[i] && this.meshes[i].visible && this.runs[i].tripId) ids.add(this.runs[i].tripId!);
    }
    return ids;
  }

  /** world position of a run's vehicle, or null when not visible */
  positionOf(i: number): THREE.Vector3 | null {
    const m = this.meshes[i];
    if (!m || !m.visible) return null;
    return m.position.clone();
  }

  /** World-space points from `fromDistM` along the run's route to the terminus. */
  aheadRoutePoints(runIndex: number, fromDistM: number): THREE.Vector3[] | null {
    const run = this.runs[runIndex];
    if (!run) return null;
    const geo = run.geometry;
    if (geo.points.length < 2) return null;
    const d = Math.max(0, Math.min(geo.total, fromDistM));
    const out: THREE.Vector3[] = [];
    const start = sampleGeometry(geo, d);
    if (start) out.push(new THREE.Vector3(start.x, start.y, start.z));
    for (let i = 0; i < geo.points.length; i++) {
      if (geo.cum[i] > d + 1e-3) {
        const p = geo.points[i];
        out.push(new THREE.Vector3(p.x, p.y, p.z));
      }
    }
    return out.length >= 2 ? out : null;
  }

  /** World-space anchor (position + tangent heading) of a run at sim time. */
  anchorAt(runIndex: number, simTime: number): { x: number; y: number; z: number; heading: number } | null {
    const run = this.runs[runIndex];
    if (!run) return null;
    let tLocal = simTime;
    if (run.period > 0) {
      tLocal = (simTime - run.offset) % run.period;
      if (tLocal < 0) tLocal += run.period;
    }
    const d = this.distanceAt(run.timeline, tLocal);
    if (d == null) return null;
    const s = sampleGeometry(run.geometry, d);
    return s ? { x: s.x, y: s.y, z: s.z, heading: s.heading } : null;
  }

  /** Full world-space route points of a run (the assigned directional track). */
  routePoints(runIndex: number): THREE.Vector3[] | null {
    const run = this.runs[runIndex];
    if (!run) return null;
    return run.geometry.points.map((p) => new THREE.Vector3(p.x, p.y, p.z));
  }

  /** Scheduled stops of a run (name, distance, arrival/departure seconds-of-day). */
  runStops(runIndex: number): RunStopInfo[] | null {
    const run = this.runs[runIndex];
    if (!run) return null;
    const out: RunStopInfo[] = [];
    for (let i = 0; i < run.stops.length; i++) {
      out.push({
        name: run.stops[i].name,
        distM: run.stops[i].distM,
        arrivalSec: run.timeline.t[2 * i] ?? null,
        departureSec: run.timeline.t[2 * i + 1] ?? null,
      });
    }
    return out;
  }

  infoFor(i: number, simTime: number): TrainInfo | null {
    const run = this.runs[i];
    if (!run) return null;
    let tLocal = simTime;
    if (run.period > 0) {
      tLocal = (simTime - run.offset) % run.period;
      if (tLocal < 0) tLocal += run.period;
    }
    const d = this.distanceAt(run.timeline, tLocal);
    if (d == null) return null;
    let currentStop: string | undefined;
    let nextStop: string | undefined;
    for (const s of run.stops) {
      if (s.distM <= d + 1) currentStop = s.name;
      else {
        nextStop = s.name;
        break;
      }
    }
    const d2 = this.distanceAt(run.timeline, tLocal + 0.5);
    const speed = d2 == null ? 0 : (d2 - d) / 0.5;
    return {
      line: run.line,
      destination: run.destination,
      tripId: run.tripId,
      routeId: run.routeId,
      currentStop,
      nextStop,
      speedKmh: Math.abs(speed) * 3.6,
      distM: d,
    };
  }

  /** runs that are within their time window at the given simulated time */
  activeCount(simTime: number): number {
    let n = 0;
    for (const run of this.runs) {
      let tLocal = simTime;
      if (run.period > 0) {
        tLocal = (simTime - run.offset) % run.period;
        if (tLocal < 0) tLocal += run.period;
      }
      const start = run.timeline.t[0];
      const end = run.timeline.t[run.timeline.t.length - 1] + run.dwell;
      if (tLocal >= start && tLocal <= end) n++;
    }
    return n;
  }

  update(simTime: number, camera?: THREE.Camera) {
    const parentScaleY = this.group.parent ? this.group.parent.scale.y : 1;
    for (let i = 0; i < this.runs.length; i++) {
      const run = this.runs[i];
      const mesh = this.meshes[i];
      const dot = this.dots[i];
      if (!mesh) continue;
      let tLocal = simTime;
      if (run.period > 0) {
        tLocal = (simTime - run.offset) % run.period;
        if (tLocal < 0) tLocal += run.period;
      }
      if (tLocal < run.timeline.t[0] || tLocal > run.timeline.t[run.timeline.t.length - 1] + run.dwell) {
        mesh.visible = false;
        if (dot) dot.visible = false;
        continue;
      }
      const d = this.distanceAt(run.timeline, tLocal);
      if (d == null) { mesh.visible = false; if (dot) dot.visible = false; continue; }
      const sample = sampleGeometry(run.geometry, d);
      if (!sample) { mesh.visible = false; if (dot) dot.visible = false; continue; }
      // The route path already follows the assigned directional track, so the
      // vehicle centre sits directly on the track centreline (only raised above
      // the rail by the body height). No lateral right-hand-running offset.
      mesh.position.set(sample.x, sample.y + 1.6, sample.z);
      mesh.rotation.y = sample.heading;
      const sel = this.selected;
      if (sel.size && !sel.has(run.line)) {
        mesh.visible = false;
        if (dot) dot.visible = false;
        continue;
      }

      const underground = sample.y < 0;
      const showAsDot = this.basemapOn && underground;
      if (dot) {
        if (showAsDot) {
          dot.position.set(sample.x, 1.5, sample.z);
          dot.visible = true;
          mesh.visible = false;
        } else {
          dot.visible = false;
          mesh.visible = true;
        }
      } else {
        mesh.visible = true;
      }

      const label = mesh.userData.label as THREE.Sprite | undefined;
      if (label && camera) {
        // label follows the body, or (underground + basemap) the surface dot
        const baseObj = showAsDot && dot ? dot : mesh;
        const base = baseObj.position;
        // Measure the distance from the camera to the train's *world* position
        // (the group is inside the vertically-scaled content root, so the local
        // y must not be compared directly against the camera's world y).
        const worldPos = new THREE.Vector3();
        baseObj.getWorldPosition(worldPos);
        const dist = camera.position.distanceTo(worldPos);
        const close = dist < this.viewingDistance;
        label.visible = close;
        if (close) {
          // constant on-screen size regardless of zoom
          const fov = (camera as THREE.PerspectiveCamera).fov ?? 55;
          const pxH = 18;
          const s = (dist * pxH * 2 * Math.tan((fov / 2) * (Math.PI / 180))) / Math.max(1, window.innerHeight);
          const aspect = (label.userData.aspect as number) || 2;
          label.position.set(base.x, base.y + 7 / parentScaleY, base.z);
          label.scale.set(s * aspect, s / parentScaleY, 1);
          (label.material as THREE.SpriteMaterial).opacity = showAsDot ? 0.5 : 1;
        }
      }
    }
  }

  private distanceAt(tl: Timeline, t: number): number | null {
    const { t: T, d: D, seg } = tl;
    if (t <= T[0]) return D[0];
    if (t >= T[T.length - 1]) return D[D.length - 1];
    let i = 1;
    while (i < T.length && T[i] < t) i++;
    const t0 = T[i - 1];
    const t1 = T[i];
    const d0 = D[i - 1];
    const d1 = D[i];
    const p = seg[i - 1];
    if (!p || t1 === t0) return d0;
    const u = t - t0;
    return d0 + Math.min(d1 - d0, profilePos(p, u));
  }
}

export function sampleGeometry(geo: RouteGeometry, dist: number) {
  const { points, cum, total } = geo;
  if (!points.length) return null;
  const d = Math.max(0, Math.min(total, dist));
  let i = 0;
  while (i < cum.length - 1 && cum[i + 1] < d) i++;
  const segLen = cum[i + 1] - cum[i] || 1;
  const t = (d - cum[i]) / segLen;
  const a = points[i];
  const b = points[i + 1] ?? a;
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
    heading: Math.atan2(b.x - a.x, b.z - a.z),
  };
}

const LABEL_BASE_H = 60;

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Freestanding train label: dark rounded background + white text, with a
 * small colour indicator so it stays readable on any line colour. The canvas
 * is sized to the text so the sprite is not wider than necessary. When a
 * realtime delay is given, an amber badge is appended on the right (extending
 * the label), not a separate floating sprite.
 */
function drawLabelCanvas(text: string, color: THREE.Color, delaySec: number | null): HTMLCanvasElement {
  const height = 64;
  const font = '700 36px system-ui, sans-serif';
  const barW = 16;
  const gap = 12;
  const pad = 12;

  const measure = document.createElement('canvas').getContext('2d')!;
  measure.font = font;
  const textW = measure.measureText(text).width;
  const textX = barW + gap;

  let delayInfo: { x: number; w: number; text: string } | null = null;
  let contentEnd = textX + textW;
  if (delaySec != null) {
    const dtext = formatDelay(delaySec);
    measure.font = font;
    const dw = measure.measureText(dtext).width;
    const dPad = 10;
    const dW = dw + dPad * 2;
    delayInfo = { x: contentEnd + 8, w: dW, text: dtext };
    contentEnd = delayInfo.x + dW;
  }
  const width = Math.ceil(contentEnd + pad + 2);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, width, height);

  roundRect(ctx, 1, 1, width - 2, height - 2, 12);
  ctx.fillStyle = 'rgba(10, 16, 22, 0.92)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.14)';
  ctx.lineWidth = 2;
  ctx.stroke();

  // colour indicator bar on the left
  ctx.fillStyle = `#${color.getHexString()}`;
  ctx.fillRect(3, 3, barW, height - 6);

  // white line number
  ctx.font = font;
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, textX, height / 2 + 1);

  if (delayInfo) {
    roundRect(ctx, delayInfo.x, 8, delayInfo.w, height - 16, 8);
    ctx.fillStyle = 'rgba(216, 118, 40, 0.95)';
    ctx.fill();
    ctx.font = font;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(delayInfo.text, delayInfo.x + delayInfo.w / 2, height / 2 + 1);
  }

  return canvas;
}

function makeLabelSprite(text: string, color: THREE.Color): THREE.Sprite {
  const canvas = drawLabelCanvas(text, color, null);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
  const sprite = new THREE.Sprite(mat);
  sprite.userData.text = text;
  sprite.userData.color = color;
  sprite.userData.aspect = canvas.width / canvas.height;
  sprite.scale.set(LABEL_BASE_H * (canvas.width / canvas.height), LABEL_BASE_H, 1);
  sprite.renderOrder = 10;
  return sprite;
}

/** Redraw a label in place with (or without) the delay extension on the right. */
function refreshLabelSprite(sprite: THREE.Sprite, delaySec: number | null) {
  const canvas = drawLabelCanvas(sprite.userData.text, sprite.userData.color, delaySec);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  const mat = sprite.material as THREE.SpriteMaterial;
  mat.map?.dispose();
  mat.map = tex;
  mat.needsUpdate = true;
  sprite.userData.aspect = canvas.width / canvas.height;
}

function formatDelay(delaySec: number): string {
  if (delaySec === 0) return '+0s';
  if (delaySec < 0) return `${delaySec}s`;
  if (delaySec < 60) return `+${delaySec}s`;
  return `+${Math.round(delaySec / 60)}m`;
}


